import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { topBarIconClass } from "./TopBar.js";
import { ChromeIconButton } from "./ChromeIconButton.js";
import { Badge } from "../atoms/index.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  type AsyncStatus,
} from "../molecules/index.js";
import type { NotificationItem } from "./notifications.types.js";
import { NotificationFeedBody } from "./NotificationFeedBody.js";

const TAB_STOPS = "a[href], button:not([disabled])";

export interface NotificationsPopoverProps {
  items: NotificationItem[];
  /**
   * Where the host's feed read stands — its `asyncStatus(...)`.
   *
   * Required for the same reason as `unreadCount`: an `items`-derived default
   * reads a failed or still-loading feed as an empty one, and the panel then
   * tells the reader they are all caught up about a list that never arrived
   * (PB-5337).
   */
  feedStatus: AsyncStatus;
  /** Refetches the feed. Absent, a failed feed renders without a Retry. */
  onRetry?: () => void;
  /**
   * The caller's TRUE unread total, which `items` cannot supply.
   *
   * `items` is one page of the feed (the server default is 50), so counting
   * unread entries in it caps the badge at the page size — the bug this prop
   * exists to fix. Required rather than optional on purpose: an optional prop
   * with a derive-from-`items` fallback would let a call site that forgets it
   * silently get the old, wrong number back.
   *
   * The library stays data-blind — it renders the number it is handed and never
   * fetches one. Deciding what to show while the count is loading or its route
   * is unavailable is the host's policy, not this component's.
   */
  unreadCount: number;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onViewAll?: () => void;
  // Optional SPA navigation for a safe notification href. Absent, the row
  // stays a plain `<a href>` and a click does a full document reload — that
  // is still correct behaviour, just not what a router-aware host wants.
  // Only an unmodified primary click is intercepted; a modified click
  // (cmd/ctrl/shift/alt, or a non-primary button) keeps the native href
  // behaviour so "open in new tab" keeps working.
  // The clicked ITEM travels with its href — see `NotificationRow`. Two
  // notifications about one workspace share an href, so identifying the row by
  // href alone is ambiguous.
  onNavigate?: (href: string, item: NotificationItem) => void;
}

export function NotificationsPopover({
  items,
  feedStatus,
  onRetry,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onViewAll,
  onNavigate,
}: NotificationsPopoverProps) {
  // Controlled so "View all" can dismiss the popover on navigate (mark-read
  // actions deliberately leave it open). Trigger/outside-click/Escape all flow
  // through onOpenChange, so toggle + dismissal behave as before.
  const [open, setOpen] = useState(false);
  const headingId = useId();
  const unreadCountId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Popover focuses only menus, so the panel takes focus itself (PB-5338).
  // Deferred a task: the content is `visibility: hidden` on its opening commit
  // until it is positioned, and a hidden element cannot take focus.
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  // For an in-panel action that closes it. Escape already restores focus; an
  // outside press deliberately leaves it on whatever was pressed.
  const dismiss = (): void => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  // For an in-panel action that can disable or unmount the control it was
  // pressed on (Mark all read at zero, Retry giving way to the loader): focus
  // would drop to <body>, out of reach of the Tab handler below (PB-5338).
  const anchored = (action: () => void) => (): void => {
    panelRef.current?.focus();
    action();
  };

  // Tab and Shift+Tab wrap inside the panel. Local rather than the
  // document-level `useFocusTrap`, which would pull focus back from a dialog
  // opened above this one (⌘K search).
  const onPanelKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const panel = panelRef.current;
    if (e.key !== "Tab" || !panel) return;
    const stops = panel.querySelectorAll<HTMLElement>(TAB_STOPS);
    const first = stops[0];
    const last = stops[stops.length - 1];
    const active = document.activeElement;
    if (!first || !last) {
      e.preventDefault();
    } else if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    // `dialog`, not the `menu` default: this panel is a notification feed — a header, a
    // scrollable list of rows and a footer link — and contains no `menuitem` at all, so
    // `role="menu"` was invalid ARIA (a menu's required children are menuitems).
    // NON-modal, so no `aria-modal`: there is no scrim, the page stays live and an
    // outside press closes it. Focus moves in, Tab stays in, and closing returns it.
    <Popover
      align="end"
      contentRole="dialog"
      open={open}
      onOpenChange={setOpen}
    >
      {/* The name is the tooltip's text, so it does not describe the
          button a second time; it stays quiet while the panel is open. */}
      <Tooltip content="Notifications" describeChild={false} disabled={open}>
        <PopoverTrigger>
          <ChromeIconButton
            ref={triggerRef}
            variant="bar"
            aria-label="Notifications"
            aria-describedby={unreadCount > 0 ? unreadCountId : undefined}
            className="relative"
          >
            <Bell className={topBarIconClass} />
            {unreadCount > 0 && (
              /* OUTSIDE the button, not inside it. A 20px glyph in a 32px
                 square (d13) leaves 6px of margin, and a count badge is 14px
                 tall and up to 20px wide — so anchored within the box it sat
                 over the bell's whole top-right quadrant and read as a hole
                 punched in the icon. Overhanging the corner drops the overlap
                 to a nick. The count is Badge circle xs critical solid
                 (OK-13); the host's description carries it, so it is hidden
                 from assistive technology. */
              <Badge
                aria-hidden="true"
                shape="circle"
                size="xs"
                tone="critical"
                variant="solid"
                data-testid="notification-unread-badge"
                className="pointer-events-none absolute -top-1 -end-1"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            )}
          </ChromeIconButton>
        </PopoverTrigger>
      </Tooltip>
      {unreadCount > 0 && (
        <span id={unreadCountId} hidden>
          {unreadCount} unread notifications
        </span>
      )}
      {/* Named by its own visible heading. A `dialog` with no accessible name is
          announced as an unlabelled dialog — the role alone tells a screen-reader user
          nothing about what they just opened. */}
      <PopoverContent
        padding="none"
        className="w-90"
        aria-labelledby={headingId}
        onKeyDown={onPanelKeyDown}
      >
        {/* The focus target; -1 keeps it out of the tab ring. */}
        <div ref={panelRef} tabIndex={-1} className="focus-ring">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-pb-border">
            <div id={headingId} className="text-sm font-semibold text-pb-text">
              Notifications
            </div>
            <button
              type="button"
              // Wrapped, never passed straight through: the prop is `() => void`,
              // so the click event must not reach the host (PB-5336).
              onClick={anchored(onMarkAllRead)}
              // Gated on the TOTAL, matching the full-page feed.
              //
              // An earlier comment here claimed the button "acts on the rows
              // rendered below it" — it does not. `onMarkAllRead` invokes the
              // watermark-bounded read-all route, which clears everything up to
              // the moment the list was drawn, not just the loaded page. Gating on
              // the page therefore contradicted the operation, and once the badge
              // started showing the true total the contradiction became visible:
              // a fully-read first page could show a positive badge beside a
              // disabled action.
              disabled={unreadCount === 0}
              className="text-dense-1 uppercase tracking-wider font-medium text-pb-accent-text hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed flex items-center gap-1"
            >
              <CheckCheck className="w-3 h-3" />
              Mark all read
            </button>
          </div>

          <NotificationFeedBody
            status={feedStatus}
            items={items}
            onMarkRead={onMarkRead}
            {...(onNavigate ? { onNavigate } : {})}
            {...(onRetry ? { onRetry: anchored(onRetry) } : {})}
            onDismiss={dismiss}
          />

          {onViewAll && (
            <div className="px-3 py-2 border-t border-pb-border">
              <button
                type="button"
                onClick={() => {
                  dismiss();
                  onViewAll();
                }}
                className="w-full text-center text-xs font-medium text-pb-accent-text hover:underline py-1"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
